import { useLocalization } from '@/context/useLocalization';
import { useEffect, useState } from 'react';
import NavLogo from '@/assets/svg/NavLogo.svg';
// import English from 'assets/gb.png';
// import Arab from 'assets/sa.png';
import Arrow from '@/assets/svg/arrow-down.svg';

import { Link, useLocation, NavLink, useNavigate, useParams } from 'react-router-dom';
import Logo from './Logo';
import { IconMenu } from '@/components/icons/MainIcons';
import ThemeBtn from '@/components/ui/ThemeBtn';
import SideMenu from './SideMenu';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { FooterProps } from '@/types/global.types';
import { getStorageUrl } from '@/utils/utils';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';

const Header = ({ data }: { data: FooterProps }) => {
    const { language, toggleLanguage } = useLocalization();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams<{ lang?: string }>();
    const { getLocalizedPath } = useLocalizedPath();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    const { t } = useTranslation();

    useEffect(() => {
        if (language === 'ar') {
            document.dir = 'rtl';
            document.querySelector('html')?.setAttribute('lang', 'ar');
            i18next.changeLanguage('ar');
        } else {
            document.dir = 'ltr';
            document.querySelector('html')?.removeAttribute('rtl');
            document.querySelector('html')?.setAttribute('lang', 'en');
            i18next.changeLanguage('en');
        }
        localStorage.setItem('lang', language);
    }, [language]);

    const handleLanguageChange = (newLang: string) => {
        if (newLang !== language) {
            // Get current path without language prefix
            const currentPath = location.pathname.replace(/^\/(en|ar)/, '') || '/';
            // Navigate to same path with new language
            navigate(`/${newLang}${currentPath === '/' ? '' : currentPath}${location.search}`, { replace: true });
            toggleLanguage();
        }
        setIsDropdownOpen(false);
    };

    const menuItems = [
        { path: '/', label: t('menu.home') },
        { path: '/about', label: t('menu.about') },
        { path: '/projects', label: t('menu.projects') },
        { path: '/partners', label: t('menu.partners') },
        { path: '#', label: t('menu.platform'), icon: NavLogo },
    ];

    return (
        <header className={`z-40`}>
            <div className="fixed w-full z-10 py-4">
                <div className="mainContainer">
                    <div className="flex justify-between items-center">
                        {/* LOGO  */}
                        <Link to={getLocalizedPath('/')}>
                            <img
                                loading="lazy"
                                src={getStorageUrl(data?.company_logo)}
                                alt={data?.company_name_ar || data?.company_name_en || 'AMAAN Logo'}
                                className="md:block hidden w-[82px]"
                            />
                            {/* <Logo className="md:block hidden w-[82px] dark:text-white text-bg-logo" /> */}
                        </Link>

                        {/* LOGO ( SMALL SCREEN )  */}
                        <div className="mainRounded md:hidden flex justify-between w-full drop-shadow-head py-[14px] px-[40px] head-border">
                            <Link to={getLocalizedPath('/')}>
                                <img
                                    loading="lazy"
                                    src={getStorageUrl(data?.company_logo)}
                                    alt={data?.company_name_ar || data?.company_name_en || 'AMAAN Logo'}
                                    className="md:hidden w-[82px]"
                                />
                            </Link>
                            <button onClick={toggleMenu}>
                                <IconMenu className="dark:text-Grey20 text-bg-logo" />
                            </button>
                        </div>

                        {/* NAVIGATION LINKS  */}
                        <div className="mainRounded py-[12px] lg:px-[32px] md:px-[25px] md:flex hidden gap-4 headLinks">
                            <ul className="flex xl:gap-[24px] lg:gap-[18px] gap-[10px] items-center">
                                <ThemeBtn />

                                {menuItems.map((item) => {
                                    const localizedPath = item.path === '#' ? '#' : getLocalizedPath(item.path);
                                    const currentPath = location.pathname.replace(/^\/(en|ar)/, '') || '/';
                                    const isActive = item.path !== '#' && currentPath === item.path;
                                    
                                    return (
                                        <li
                                            key={item.path}
                                            className={`xl:text-[16px] lg:text-[14px] text-[12.6px] font-medium ${
                                                isActive
                                                    ? 'text-main'
                                                    : 'dark:text-Grey40 text-bg-gray'
                                            } flex items-center lg:gap-1 gap-[2px]`}
                                        >
                                            {item.icon && (
                                                <img
                                                    loading="lazy"
                                                    src={item.icon}
                                                    alt={`${item.label} icon`}
                                                    className="max-w-[32px] max-h-[24px] filter invert dark:invert-0"
                                                />
                                            )}
                                            {item.path === '#' ? (
                                                <span>{item.label}</span>
                                            ) : (
                                                <Link to={localizedPath}>{item.label}</Link>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        {/* SWITCH LANGUAGE  */}
                        <div className="relative hidden md:block">
                            <button
                                onClick={toggleDropdown}
                                className="border lg:px-6 px-4 py-[12px] cursor-pointer langBorder flex items-center lg:gap-2 gap-[6px] rounded-[70px]"
                            >
                                <img
                                    loading="lazy"
                                    src={language === 'ar' ? '/assets/sa.png' : '/assets/gb.png'}
                                    alt={language === 'ar' ? 'العربية - Current Language' : 'English - Current Language'}
                                    className="w-6 h-6"
                                />
                                <h2 className="md:text-[16px] text-[14px] font-bold leading-3 text-Grey20">
                                    {language === 'ar' ? 'العربية' : 'English'}
                                </h2>
                                <img
                                    loading="lazy"
                                    src={Arrow}
                                    alt="Language Dropdown Arrow"
                                    className={`ml-2 transform transition-transform ${
                                        isDropdownOpen ? 'rotate-180' : 'rotate-0'
                                    }`}
                                />
                            </button>

                            {isDropdownOpen && (
                                <div className="absolute right-1 mt-2 headLinks shadow-md rounded-2xl py-2 px-6 z-50">
                                    <button
                                        onClick={() => handleLanguageChange('en')}
                                        className="flex items-center lg:px-4 px-2 py-4 gap-2"
                                    >
                                        <span>
                                            <img loading="lazy" src={'/assets/gb.png'} alt="English" className="" />
                                        </span>
                                        <div className="text-bg-gray dark:text-Grey20 md:text-[16px] text-[14px]">
                                            English
                                        </div>
                                    </button>
                                    <button
                                        className="flex items-center lg:px-4 px-2 py-4 gap-2"
                                        onClick={() => handleLanguageChange('ar')}
                                    >
                                        <span>
                                            <img loading="lazy" src={'/assets/sa.png'} alt="العربية" className="" />
                                        </span>
                                        <div className="text-bg-gray dark:text-Grey20 md:text-[16px] text-[14px]">
                                            العربية
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MENU FOR SMALL SCREENS */}
            {isMenuOpen && (
                <SideMenu
                    isMenuOpen={isMenuOpen}
                    toggleMenu={toggleMenu}
                    menuItems={menuItems}
                    toggleLanguage={toggleLanguage}
                    language={language}
                    isDropdownOpen={isDropdownOpen}
                    handleLanguageChange={handleLanguageChange}
                />
            )}
        </header>
    );
};

export default Header;
